import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CloudSun, Loader2, Droplets, Thermometer, Wind, Lightbulb, CloudRain, Languages } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANGUAGES } from "@/lib/languages";
import { toast } from "sonner";

export default function Weather() {
  const { user, updateLanguage } = useAuth();
  const [location, setLocation] = useState("");
  const [language, setLanguage] = useState(user?.language || "English");
  const changeLanguage = (l) => { setLanguage(l); updateLanguage(l); };
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchWeather = async () => {
    if (!location.trim()) return toast.error("Enter a location");
    setLoading(true);
    setData(null);
    try {
      const res = await apiClient.get("/weather", { params: { location, language } });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight mb-2">Weather Advice</h1>
        <p className="text-stone-600 mb-2">Live weather with AI-powered farm guidance for your area.</p>
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-block mb-8">
          Real-time forecast powered by Open-Meteo. Advice is AI-generated for your conditions.
        </p>

        <div className="bg-white rounded-2xl border border-stone-200 p-6 flex flex-col sm:flex-row gap-3 sm:items-end mb-8">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Location</Label>
            <Input data-testid="weather-location-input" value={location} onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchWeather()} placeholder="e.g. Nairobi, Kenya" className="mt-1.5 bg-white" />
          </div>
          <div className="sm:w-48">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1"><Languages className="h-3 w-3" /> Language</Label>
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger data-testid="weather-language-select" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="get-weather-btn" onClick={fetchWeather} disabled={loading} className="h-11 px-6 bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-xl font-semibold">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CloudSun className="h-5 w-5 mr-2" /> Get advice</>}
          </Button>
        </div>

        {data && (
          <div className="space-y-6" data-testid="weather-results">
            {/* Current */}
            <div className="bg-[#143D29] text-emerald-50 rounded-2xl p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-6">
                <div>
                  <p className="text-emerald-200/70 text-sm uppercase tracking-wider font-semibold">{data.location}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-mono-tech text-5xl font-bold">{Math.round(data.current?.temp_c)}°</span>
                    <span className="text-emerald-100/80">{data.current?.condition}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <Droplets className="h-5 w-5 mx-auto text-[#52B788] mb-1" />
                    <div className="font-mono-tech font-bold">{data.current?.humidity}%</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-200/60">Humidity</div>
                  </div>
                  <div>
                    <Wind className="h-5 w-5 mx-auto text-[#52B788] mb-1" />
                    <div className="font-mono-tech font-bold text-sm">{data.current?.soil_moisture}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-200/60">Soil</div>
                  </div>
                  <div>
                    <Thermometer className="h-5 w-5 mx-auto text-[#52B788] mb-1" />
                    <div className="font-mono-tech font-bold">{Math.round(data.current?.temp_c)}°C</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-200/60">Temp</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Forecast */}
            {data.forecast?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {data.forecast.map((d, i) => (
                  <div key={i} data-testid={`forecast-${i}`} className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
                    <div className="font-semibold text-emerald-950">{d.day}</div>
                    <CloudRain className="h-8 w-8 mx-auto my-3 text-[#3B82F6]" />
                    <div className="font-mono-tech text-lg font-bold text-emerald-950">{Math.round(d.high_c)}° <span className="text-stone-400 text-sm">/ {Math.round(d.low_c)}°</span></div>
                    <div className="text-sm text-stone-600 mt-1">{d.condition}</div>
                    <div className="text-xs text-blue-600 font-mono-tech mt-1">{d.rain_mm}mm rain</div>
                  </div>
                ))}
              </div>
            )}

            {/* Advice */}
            {data.advice?.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="font-display text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2"><Lightbulb className="h-5 w-5 text-[#D97706]" /> Today's farm advice</h3>
                <ul className="space-y-3">
                  {data.advice.map((a, i) => (
                    <li key={i} data-testid={`advice-${i}`} className="flex gap-2 text-stone-700"><span className="text-[#52B788] mt-0.5">•</span>{a}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
